/**
 * Collect ServiceNow's OAuth application and integration-user credentials.
 *
 * Administrators can replace the complete workspace bundle but can never read
 * it back. The UMD source exposes only encrypted-key presence, and the warning
 * prevents personal university credentials from entering either shared store.
 */
import { Shield } from 'lucide-react'

import {
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui'
import { cn } from '@/lib/utils'
import type { ConnectorCredentialSource } from '../../types'
import type {
  ServiceNowConnectionStatus,
  ServiceNowWorkspaceCredentials,
} from '../types'

export function ServiceNowCredentials({
  source,
  onSourceChange,
  connection,
  credentials,
  onCredentialsChange,
  canSelectSource,
  canManageCredential,
}: {
  source: ConnectorCredentialSource
  onSourceChange: (source: ConnectorCredentialSource) => void
  connection: ServiceNowConnectionStatus
  credentials: ServiceNowWorkspaceCredentials
  onCredentialsChange: (credentials: ServiceNowWorkspaceCredentials) => void
  canSelectSource: boolean
  canManageCredential: boolean
}) {
  const field = (
    key: keyof ServiceNowWorkspaceCredentials,
    label: string,
  ) => (
    <div className="space-y-1.5">
      <Label
        htmlFor={`servicenow-${key}`}
        className="text-xs font-medium text-slate-700"
      >
        {label}
      </Label>
      <Input
        id={`servicenow-${key}`}
        type="password"
        autoComplete="off"
        value={credentials[key]}
        disabled={!canManageCredential}
        onChange={event => onCredentialsChange({
          ...credentials,
          [key]: event.target.value,
        })}
        placeholder={connection.workspaceConfigured
          ? '•••••••• — leave blank to keep'
          : `Enter ${label.toLowerCase()}`}
        className="h-9 text-[13px]"
      />
    </div>
  )

  const workspaceStatus = connection.workspaceConfigured
    ? 'OAuth app + system account, stored'
    : 'Not configured'
  const umdStatus = connection.umdConfigured
    ? 'OAuth app + system account, stored'
    : 'Not configured'
  const sources = connection.workspaceConfigured === null
    ? ([['umd', 'UMD Credential Store', umdStatus]] as const)
    : ([
        ['workspace', 'Workspace credential', workspaceStatus],
        ['umd', 'UMD Credential Store', umdStatus],
      ] as const)

  return (
    <div className="space-y-3">
      <RadioGroup
        value={source}
        disabled={!canSelectSource}
        onValueChange={value => onSourceChange(value as ConnectorCredentialSource)}
        className={`grid gap-2.5 ${sources.length > 1 ? 'sm:grid-cols-2' : ''}`}
      >
        {sources.map(([value, title, status]) => (
          <label
            key={value}
            className={cn(
              'flex cursor-pointer items-center gap-3 rounded-[9px] border px-3 py-3 transition-colors',
              source === value
                ? 'border-[1.5px] border-slate-900 bg-white'
                : 'border-slate-200 hover:bg-slate-50',
            )}
          >
            <RadioGroupItem value={value} />
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold text-slate-900">
                {title}
              </span>
              <span className="block truncate text-xs text-slate-500">{status}</span>
            </span>
          </label>
        ))}
      </RadioGroup>

      {source === 'workspace'
        ? (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              {field('clientId', 'OAuth client ID')}
              {field('clientSecret', 'OAuth client secret')}
              {field('username', 'System username')}
              {field('password', 'System password')}
            </div>
            {connection.workspaceConfigured && (
              <p className="text-xs text-muted-foreground">
                Leave all four fields blank to keep the stored bundle, or
                complete all four fields to replace it.
              </p>
            )}
          </div>
          )
        : (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-500">
              Managed by the credential-store administrator:
              <div className="mt-1 space-y-0.5 break-all font-mono text-slate-700">
                {connection.umdCredentialKeys.map(key => <div key={key}>{key}</div>)}
              </div>
            </div>
          )}

      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2.5 text-xs leading-4 text-amber-900">
        <Shield className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Use the OAuth application and system account issued for this integration — never a personal UMD password or token.
        </span>
      </div>
    </div>
  )
}
